import React, { useEffect, useRef, useState } from "react";
import { Collapse, Button } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler } from "@preveil-api";
import { AdminUIActionTypes, AdminErrorMessages, useCsvDataValidation, FileTemplates, CSVUploadStatusHeading, EmailGatewayEntity, RegexHelper } from "src/common";
import { DraggableContainer, Icon } from "src/components";
import { AddressPanel } from "..";
import _ from "lodash";
import { ParseError, ParseMeta } from "papaparse";

type AllProps = {
  handleAction: ActionHandlerFunction;
  address_list: string[];
  submitAddresses: Function;
}

type PapaparseData = {
  data: {
    "Domain or User Address": string;
    "Display Name": string;
  }
}

function GroupFormComponent(props: AllProps) {
  const { handleAction, address_list, submitAddresses } = props;
  const csv_column_names = ["Domain or User Address", "Display Name"]; // column name that matches the document template provided to the user.
  const [open_collapse, setOpenCollapse] = useState<boolean>(false);
  const csv_data_ref = useRef<Map<string, EmailGatewayEntity>>();
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation(address_list);
  const is_valid = !_.isEmpty(valid_addresses);

  useEffect(() => {
    return () => {  
      resetData();
    };
  }, []);

  function formatAddresses(addresses: string[]): EmailGatewayEntity[] {
    return addresses.map((address: string) => {
      return !!csv_data_ref.current ? csv_data_ref.current.get(address) : undefined;
    }).filter((addr): addr is EmailGatewayEntity => addr !== undefined);
  }

  const GroupFormRequests = {
    // Handle change for individual addresses/domain tab
    handleSubmitAddresses: (collection?: string[]) => {
      submitAddresses(collection);
    },
    // Handle change when using CSV Upload
    handleSubmitCsvData: () => {
       const formatted_addresses = valid_addresses.map((address: string) => {
        return !!csv_data_ref.current ? csv_data_ref.current.get(address) : undefined;
      }).filter((addr): addr is EmailGatewayEntity => addr !== undefined);
      submitAddresses(formatted_addresses);
      resetData();
    },
    handleReset: () => {
      resetData();
    },
    handleShowModal: () => {
      handleAction({ actionType: AdminUIActionTypes.ShowModal });
      resetData();
    },
    handleCsvData: (csv_file: { data: PapaparseData[], errors: ParseError[], meta: ParseMeta }) => {
      handleCsvFileData(csv_file);
    },
    // Handle error from Draggable component (wrong file extension).
    handlePageError: (params: { message: string, stack: string }) => {
      const { message, stack } = params;
      handleAction({ actionType: AdminUIActionTypes.PageError, params: { message, stack } });
    }
  };

  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    if ((GroupFormRequests as any)[callback] instanceof Function) {
      (GroupFormRequests as any)[callback](actionObj.params);
    }
  }

  // handling csv data.
  function handleCsvFileData(csv_file: Papa.ParseResult<any>) {
    if (csv_file?.errors && csv_file?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      handleAction({ actionType: AdminUIActionTypes.PageErrorMessage, params: { message: AdminErrorMessages.error_csv_upload_format, stack: csv_file.errors } });
    }
    const { data, meta } = csv_file;
    const { fields } = meta;
    const csv_columns = new Set(fields);
    // we need to verify if the columns name inside the csv file matches the ones in the csv template.
    const is_valid_columns = csv_columns.has(csv_column_names[0]) && csv_columns.has(csv_column_names[1]);

    if (data.length === 0 || !is_valid_columns) {
      if (data.length === 0) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_empty_gateway_file, stack: data } });
      } else if (!is_valid_columns) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_wrong_heading, stack: meta.fields } });
      }
    } else {
      // any repeated address/domain from the csv file will be removed (only within the document).
      const address_column = csv_column_names[0];
      const name_column = csv_column_names[1];
      const csv_data_map = new Map<string, EmailGatewayEntity>(data.map((address: any) => {
        const key = address[address_column];
        const email_address = address[address_column];
        const display_name = address[name_column] || email_address;
        const type = RegexHelper.testDomain(email_address) ? "domain" : "individual";
        return [key, { email_address, display_name, type }];
      }));
      csv_data_ref.current = csv_data_map; // Keeping a map of all the aliases (address, name) in a ref to persist between states, so we can use as a reference when submitting the data.
      const addresses_from_file = Array.from(csv_data_map.keys());
      handleValidateData(addresses_from_file);
    }
  }

  // Description: Handle delete of a single valid address
  function handleDeleteAction(id: string | undefined) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  };

  return <>
    {valid_addresses.length >= 1 && is_data_drop && (
      <AddressPanel
        title={CSVUploadStatusHeading.valid_gateway}
        address_list={formatAddresses(valid_addresses)}
        handleDelete={handleDeleteAction}
      />
    )}
    {!(_.isEmpty(invalid_addresses) && _.isEmpty(duplicate_addresses)) && (
      <div className={`invalid-card ${is_valid ? "show-divider" : ""}`}>
        <header>
          <h3 className="mb-3">
            Aliases that will <i className="emphasis">not</i> be added
          </h3>
          <Button
            variant="icon"
            onClick={() => setOpenCollapse(!open_collapse)}
            aria-controls="collapse-invalid-area"
            >
            <Icon className={`${open_collapse ? "ficon-chevron-up" : "ficon-chevron-down"}`}/>
          </Button>
        </header>
        <Collapse in={open_collapse}>
          <div>
              {duplicate_addresses.length >= 1 && is_data_drop && (
                <AddressPanel
                  title={CSVUploadStatusHeading.duplicate_alias}
                  address_list={formatAddresses(duplicate_addresses)}
                  error={true}
                  handleDelete={handleDeleteAction}
                />
              )}
              {invalid_addresses.length >= 1 && is_data_drop && (
                <AddressPanel
                  title={CSVUploadStatusHeading.invalid}
                  address_list={formatAddresses(invalid_addresses)}
                  error={true}
                  handleDelete={handleDeleteAction}
                />
              )}
          </div>
        </Collapse>
      </div>
    )}
    {!is_data_drop && (
      <div className="mb-3">
        <p>
          Download and use the upload template to accurately add multiple users at once
          <a
            href={FileTemplates.email_gateway_users}
            target="_blank"
            rel="noreferrer">
            <Icon className="ficon-download small"/>
            Preveil-Email-Gateway-Upload-Template.csv
          </a>
        </p>
        <DraggableContainer handleAction={handlePageActions} />
      </div>
    )}
    <div className="btn-panel">
      <Button id="submit_button"
        onClick={() => GroupFormRequests.handleSubmitCsvData()}
        disabled={!is_valid}
      >
        Add to List
      </Button>
      <Button
        id="cancel_button"
        variant="no-outline-primary"
        onClick={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })}
      >
        Cancel
      </Button>
    </div>
  </>;
}

export default React.memo(GroupFormComponent);
